import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Check, Trash2, Loader2, ShieldCheck, PlusCircle, Pencil, X, LogOut } from "lucide-react";
import { Logo } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-obsidian/80 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-ember/60 focus:ring-2 focus:ring-ember/20";

const StarPicker = ({ value, onChange, prefix }) => (
  <div className="flex items-center gap-1.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={`${prefix}-star-${n}`}
        onClick={() => onChange(n)}
        className="p-1 transition-transform duration-150 hover:scale-110"
        aria-label={`${n} étoiles`}
      >
        <Star size={22} className={n <= value ? "text-gold" : "text-slate-600"} fill={n <= value ? "currentColor" : "none"} />
      </button>
    ))}
  </div>
);

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem("admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ parent_name: "", rating: 5, message: "" });
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ parent_name: "", rating: 5, message: "" });

  const headers = () => ({ headers: { "X-Admin-Key": key } });

  const load = async (k) => {
    try {
      const r = await axios.get(`${API}/admin/testimonials`, { headers: { "X-Admin-Key": k } });
      setItems(r.data);
      setAuthed(true);
      setError("");
      if (k) sessionStorage.setItem("admin_key", k);
    } catch (e) {
      sessionStorage.removeItem("admin_key");
      if (!authed) setError("Clé incorrecte ou serveur indisponible.");
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    sessionStorage.removeItem("admin_key");
    setAuthed(false);
    setItems(null);
    setKey("");
  };

  const approve = async (id) => {
    setBusy(id);
    await axios.post(`${API}/admin/testimonials/${id}/approve`, {}, headers()).catch(() => {});
    setBusy(null);
    load(key);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce témoignage ?")) return;
    setBusy(id);
    await axios.delete(`${API}/admin/testimonials/${id}`, headers()).catch(() => {});
    setBusy(null);
    load(key);
  };

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await axios.post(`${API}/admin/testimonials`, addForm, headers());
      setAddForm({ parent_name: "", rating: 5, message: "" });
      setShowAdd(false);
      load(key);
    } catch (err) {
      setError("Ajout impossible. Réessayez.");
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setEditForm({ parent_name: t.parent_name, rating: t.rating || 5, message: t.message });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(editingId);
    try {
      await axios.put(`${API}/admin/testimonials/${editingId}`, editForm, headers());
      setEditingId(null);
      load(key);
    } catch (err) {
      setError("Modification impossible. Réessayez.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-chalk font-sans px-5 py-16">
      <div className="max-w-3xl mx-auto" data-testid="admin-page">
        <div className="flex items-center justify-between gap-3 mb-10">
          <div className="flex items-center gap-3">
            <Logo size={40} />
            <div>
              <h1 className="font-heading text-2xl font-extrabold uppercase">Validation des témoignages</h1>
              <p className="text-xs text-slate-500">Espace réservé à Yassine</p>
            </div>
          </div>
          {authed && (
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-ember px-4 py-2 text-xs font-bold uppercase tracking-wide text-slate-300 transition-colors duration-200"
            >
              <LogOut size={13} />
              Sortir
            </button>
          )}
        </div>

        {!authed ? (
          <div className="rounded-3xl border border-white/10 bg-coal p-8 max-w-md">
            <form data-testid="admin-login-form" onSubmit={(e) => { e.preventDefault(); load(key); }}>
              <label htmlFor="admin-key" className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                Clé d'accès
              </label>
              <input
                id="admin-key"
                data-testid="admin-key-input"
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                className={inputCls}
                placeholder="Votre clé administrateur"
              />
              {error && <p className="mt-3 text-sm text-ember" data-testid="admin-error">{error}</p>}
              <button
                type="submit"
                data-testid="admin-login-button"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-ember hover:bg-emberdark px-6 py-3.5 text-xs font-bold uppercase tracking-wide transition-colors duration-200"
              >
                <ShieldCheck size={15} />
                Accéder
              </button>
            </form>
          </div>
        ) : (
          <div className="space-y-4">
            <button
              data-testid="admin-add-toggle"
              onClick={() => setShowAdd(!showAdd)}
              className="inline-flex items-center gap-2 rounded-full bg-gold text-obsidian hover:bg-amber-400 px-6 py-3 text-xs font-bold uppercase tracking-wide transition-colors duration-200"
            >
              <PlusCircle size={15} />
              Ajouter un témoignage reçu
            </button>

            {showAdd && (
              <form onSubmit={add} data-testid="admin-add-form" className="rounded-2xl border border-gold/30 bg-coal p-6 space-y-4">
                <p className="text-xs text-slate-400 leading-relaxed">
                  Pour publier un témoignage que vous avez déjà reçu (par message, email…) — il
                  apparaît immédiatement sur le site. N'oubliez pas d'avoir l'accord de la famille.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <input
                    data-testid="admin-add-name"
                    required
                    value={addForm.parent_name}
                    onChange={(e) => setAddForm({ ...addForm, parent_name: e.target.value })}
                    className={inputCls}
                    placeholder="Nom affiché (ex. Sophie M.)"
                  />
                  <StarPicker value={addForm.rating} onChange={(r) => setAddForm({ ...addForm, rating: r })} prefix="admin-add" />
                </div>
                <textarea
                  data-testid="admin-add-message"
                  required
                  rows={3}
                  value={addForm.message}
                  onChange={(e) => setAddForm({ ...addForm, message: e.target.value })}
                  className={`${inputCls} resize-none`}
                  placeholder="Le témoignage tel que reçu…"
                />
                <button
                  type="submit"
                  data-testid="admin-add-submit"
                  disabled={adding}
                  className="inline-flex items-center gap-2 rounded-full bg-ember hover:bg-emberdark disabled:opacity-60 px-6 py-3 text-xs font-bold uppercase tracking-wide transition-colors duration-200"
                >
                  {adding && <Loader2 size={13} className="animate-spin" />}
                  Publier directement
                </button>
              </form>
            )}

            {items && items.length === 0 && (
              <p className="text-sm text-slate-400 rounded-2xl border border-dashed border-white/15 p-8 text-center" data-testid="admin-empty">
                Aucun témoignage pour le moment.
              </p>
            )}
            {items && items.map((t) => (
              <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="rounded-2xl border border-white/10 bg-coal p-6">
                {editingId === t.id ? (
                  <form onSubmit={saveEdit} data-testid={`admin-edit-form-${t.id}`} className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <input
                        data-testid={`admin-edit-name-${t.id}`}
                        required
                        value={editForm.parent_name}
                        onChange={(e) => setEditForm({ ...editForm, parent_name: e.target.value })}
                        className={inputCls}
                        placeholder="Nom affiché"
                      />
                      <StarPicker value={editForm.rating} onChange={(r) => setEditForm({ ...editForm, rating: r })} prefix={`admin-edit-${t.id}`} />
                    </div>
                    <textarea
                      data-testid={`admin-edit-message-${t.id}`}
                      required
                      rows={4}
                      value={editForm.message}
                      onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                      className={`${inputCls} resize-none`}
                    />
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        data-testid={`admin-edit-save-${t.id}`}
                        disabled={busy === t.id}
                        className="inline-flex items-center gap-2 rounded-full bg-gold text-obsidian hover:bg-amber-400 px-5 py-2.5 text-xs font-bold uppercase tracking-wide disabled:opacity-60 transition-colors duration-200"
                      >
                        {busy === t.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                        Enregistrer
                      </button>
                      <button
                        type="button"
                        data-testid={`admin-edit-cancel-${t.id}`}
                        onClick={() => setEditingId(null)}
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-white/50 px-5 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors duration-200"
                      >
                        <X size={13} />
                        Annuler
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-100">{t.parent_name}</p>
                        <div className="flex gap-0.5 mt-1">
                          {Array.from({ length: t.rating || 5 }).map((_, s) => (
                            <Star key={s} size={13} className="text-gold" fill="currentColor" />
                          ))}
                        </div>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${
                        t.approved ? "bg-gold/15 text-amber-300 border border-gold/30" : "bg-ember/15 text-red-300 border border-ember/30"
                      }`}>
                        {t.approved ? "Publié" : "En attente"}
                      </span>
                    </div>
                    <p className="mt-4 text-sm text-slate-300 leading-relaxed whitespace-pre-line">« {t.message} »</p>
                    <div className="mt-5 flex flex-wrap gap-3">
                      {!t.approved && (
                        <button
                          data-testid={`admin-approve-${t.id}`}
                          onClick={() => approve(t.id)}
                          disabled={busy === t.id}
                          className="inline-flex items-center gap-2 rounded-full bg-gold text-obsidian hover:bg-amber-400 px-5 py-2.5 text-xs font-bold uppercase tracking-wide disabled:opacity-60 transition-colors duration-200"
                        >
                          {busy === t.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                          Publier
                        </button>
                      )}
                      <button
                        data-testid={`admin-edit-${t.id}`}
                        onClick={() => startEdit(t)}
                        disabled={busy === t.id}
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-gold hover:text-amber-300 px-5 py-2.5 text-xs font-bold uppercase tracking-wide disabled:opacity-60 transition-colors duration-200"
                      >
                        <Pencil size={13} />
                        Modifier
                      </button>
                      <button
                        data-testid={`admin-delete-${t.id}`}
                        onClick={() => remove(t.id)}
                        disabled={busy === t.id}
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-ember hover:text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wide disabled:opacity-60 transition-colors duration-200"
                      >
                        <Trash2 size={13} />
                        Supprimer
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
